import React, {
  lazy,
  Suspense,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useCan, useOrganizationId } from '@features/user';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import { Box, Button, Paper, Typography } from '@mui/material';
import {
  FeatureGates,
  useEventTracking,
  useFeatureGate,
} from '@services/statsig';
import { useSnackbar } from 'notistack';

import type { CleanupApplyResult } from '../hooks/useCleanupApply';
import { useCleanupApply } from '../hooks/useCleanupApply';
import { useCleanupWizardData } from '../hooks/useCleanupWizardData';

import type { CleanupWizardProps } from './CleanupWizard';

/** Human summary of what the apply run actually did. */
const describeApplyResult = (r: CleanupApplyResult): string => {
  const done: string[] = [];
  const merged = r.promotedCount + r.mergedCount;
  if (merged) done.push(`${merged} consolidated`);
  if (r.archivedCount) done.push(`${r.archivedCount} archived`);
  if (r.restoredCount) done.push(`${r.restoredCount} restored`);
  if (r.renamedCount) done.push(`${r.renamedCount} renamed`);

  const applied = done.length
    ? `Applied: ${done.join(', ')}${r.skippedCount ? ` (${r.skippedCount} skipped)` : ''}.`
    : 'No changes to apply.';
  if (r.failedSteps.length) {
    return `${applied} Couldn't finish ${r.failedSteps.join(' and ')} — safe to try again.`;
  }
  return applied;
};

const CleanupWizardDialog = lazy(() =>
  import('./CleanupWizardDialog').then((module) => ({
    default: module.CleanupWizardDialog,
  }))
);

/** Live bootstrap read (GDN-2296); the fixture survives only in stories/tests. */
const DATA_SOURCE = 'live';
const ENTRY_SOURCE = 'practice_appointment_types_page';

/**
 * Container for the practice cleanup wizard (GDN-2295): the entry card on
 * the appointment types page plus the fullscreen dialog it opens. Owns the
 * feature gate, the data-source seam (fixture until GDN-2296), and the
 * Statsig funnel (opened → step_submitted → apply_clicked → closed). Apply
 * is preview-only until the engine verbs land (GDN-2292/93/94).
 */
export const CleanupWizardEntry: React.FC = () => {
  const isEnabled = useFeatureGate(FeatureGates.PRACTICE_CLEANUP_WIZARD);
  const canManageAppointmentTypes = useCan('manage_appointment_types');
  const organizationId = useOrganizationId() ?? undefined;
  const { practiceCleanup } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const openedAtRef = useRef<number | null>(null);
  const lastStepRef = useRef<{
    label: string;
    index: number;
    total: number;
  } | null>(null);
  const appliedRef = useRef(false);

  const dataState = useCleanupWizardData({ orgId: organizationId, skip: !open });
  const applyCleanup = useCleanupApply(organizationId);

  const handleOpen = useCallback(() => {
    openedAtRef.current = Date.now();
    lastStepRef.current = null;
    appliedRef.current = false;
    setOpen(true);
    practiceCleanup.wizardOpened({
      organization_id: organizationId,
      source: ENTRY_SOURCE,
      data_source: DATA_SOURCE,
    });
  }, [organizationId, practiceCleanup]);

  const handleStepChange = useCallback<
    NonNullable<CleanupWizardProps['onStepChange']>
  >(
    (step) => {
      const previous = lastStepRef.current;
      lastStepRef.current = step;
      // Only a forward move means the previous step was submitted; the
      // initial mount and back-navigation are not completions.
      if (previous && step.index > previous.index) {
        practiceCleanup.stepSubmitted({
          organization_id: organizationId,
          step_label: previous.label,
          step_index: previous.index,
          total_steps: step.total,
        });
      }
    },
    [organizationId, practiceCleanup]
  );

  const handleClose = useCallback(() => {
    const openedAt = openedAtRef.current;
    practiceCleanup.wizardClosed({
      organization_id: organizationId,
      last_step_label: lastStepRef.current?.label,
      last_step_index: lastStepRef.current?.index,
      total_steps: lastStepRef.current?.total,
      applied: appliedRef.current,
      duration_seconds:
        openedAt === null
          ? undefined
          : Math.round((Date.now() - openedAt) / 1000),
    });
    setOpen(false);
  }, [organizationId, practiceCleanup]);

  const handleApply = useCallback<NonNullable<CleanupWizardProps['onApply']>>(
    async (plan) => {
      practiceCleanup.applyClicked({
        organization_id: organizationId,
        archive_count: plan.archiveTypeIds.length,
        restore_count: plan.restoreTypeIds.length,
        merge_count: plan.orgMerges.length,
        promote_count: plan.pairPromotions.length,
        rename_count: plan.renames.length,
        data_source: DATA_SOURCE,
      });
      const result = await applyCleanup(plan);
      const anyApplied =
        result.archivedCount ||
        result.restoredCount ||
        result.promotedCount ||
        result.mergedCount ||
        result.renamedCount;
      appliedRef.current = Boolean(anyApplied);
      enqueueSnackbar(describeApplyResult(result), {
        variant: result.failedSteps.length
          ? 'warning'
          : anyApplied
            ? 'success'
            : 'info',
      });
      // Keep the wizard open on failure so the admin can retry; otherwise close.
      if (!result.failedSteps.length) handleClose();
    },
    [
      applyCleanup,
      enqueueSnackbar,
      handleClose,
      organizationId,
      practiceCleanup,
    ]
  );

  if (!isEnabled || !canManageAppointmentTypes) return null;

  return (
    <>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Clean up appointment types
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, maxWidth: 680 }}
            >
              Merge duplicate visit types, archive the ones nobody uses, and
              build one shared catalog for the whole practice. You review every
              suggestion before anything changes.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<CleaningServicesIcon />}
            onClick={handleOpen}
            sx={{ justifySelf: { xs: 'stretch', md: 'end' } }}
          >
            Start cleanup
          </Button>
        </Box>
      </Paper>

      {open && (
        <Suspense fallback={null}>
          <CleanupWizardDialog
            open
            onClose={handleClose}
            state={dataState}
            onApply={handleApply}
            onStepChange={handleStepChange}
          />
        </Suspense>
      )}
    </>
  );
};
